import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Calendar, Clock, MapPin, Phone, Mail, MessageCircle, XCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingConfirmation = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [booking, setBooking] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const checkPayment = async () => {
      try {
        let paid = false;
        if (sessionId) {
          const payRes = await fetch(`${API_URL}/api/bookings/payment-status/${sessionId}`);
          const payData = await payRes.json();
          paid = payData.status === 'complete';
        }
        const res = await fetch(`${API_URL}/api/bookings/${bookingId}`);
        if (cancelled) return;
        if (!res.ok) { setError('Booking not found'); setStatus('failed'); return; }
        const data = await res.json();
        setBooking(data);
        if (data.status === 'cancelled' || ['refunded', 'refund_failed'].includes(data.payment_status)) {
          setError(data.cancel_reason
            ? 'Your payment arrived after the 10-minute hold ended and the slot had been taken, so your payment is being fully refunded. Please book another time.'
            : 'This booking has been cancelled and refunded.');
          setStatus('failed');
        } else if (paid || data.payment_status === 'paid') {
          setStatus('success');
        } else {
          setStatus('failed');
        }
      } catch (e) {
        if (!cancelled) { setError('Error loading booking'); setStatus('failed'); }
      }
    };
    checkPayment();
    return () => { cancelled = true; };
  }, [bookingId, sessionId]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-[#b8860b] mx-auto mb-4" />
          <p className="text-[#475569]">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'failed' || !booking) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <XCircle size={56} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-[#0f172a] mb-2" data-testid="confirmation-failed-title">{booking && (booking.status === 'cancelled' || ['refunded', 'refund_failed', 'refunding'].includes(booking.payment_status)) ? 'Booking Cancelled & Refunded' : 'Payment Not Completed'}</h2>
          <p className="text-[#475569] mb-6">{error || 'Your payment was not completed. The slot has been released.'}</p>
          <Link to="/book" className="inline-block bg-[#0f172a] text-white px-6 py-3 hover:bg-[#b8860b] transition-colors" data-testid="try-again-btn">
            Try Again
          </Link>
        </div>
      </div>
    );
  }

  const serviceName = booking.service === 'home_visit' ? 'Home Visit' : 'Telemedicine';

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="confirmation-page">
      <div className="bg-[#0f172a] text-white py-6 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <CheckCircle size={48} className="text-[#22c55e] mx-auto mb-3" />
          <h1 className="text-2xl font-semibold mb-1">Booking Confirmed</h1>
          <p className="text-[#94a3b8]">Booking Reference: {booking.booking_id}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white border border-[#e2e8f0] p-6 mb-6">
          <h2 className="font-semibold text-[#0f172a] text-lg mb-4">Appointment Details</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Calendar size={18} className="text-[#b8860b] mt-0.5" />
              <div>
                <div className="text-sm text-[#64748b]">Date</div>
                <div className="text-[#0f172a] font-medium">{formatDate(booking.date)}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={18} className="text-[#b8860b] mt-0.5" />
              <div>
                <div className="text-sm text-[#64748b]">Time (Ibiza)</div>
                <div className="text-[#0f172a] font-medium">{booking.time} — 30-minute consultation</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              {booking.service === 'home_visit' ? <MapPin size={18} className="text-[#b8860b] mt-0.5" /> : <Phone size={18} className="text-[#b8860b] mt-0.5" />}
              <div>
                <div className="text-sm text-[#64748b]">Service</div>
                <div className="text-[#0f172a] font-medium">{serviceName}</div>
                {booking.service === 'home_visit' && booking.address && (
                  <div className="text-sm text-[#475569] mt-1">{booking.address}</div>
                )}
                {booking.service === 'telemedicine' && (
                  <div className="text-sm text-[#475569] mt-1">Video / phone consultation</div>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail size={18} className="text-[#b8860b] mt-0.5" />
              <div>
                <div className="text-sm text-[#64748b]">Amount Paid</div>
                <div className="text-[#0f172a] font-medium">€{booking.total_amount?.toFixed(0)}</div>
                {booking.distance_supplement > 0 && (
                  <div className="text-xs text-[#64748b]">Includes €{booking.distance_supplement} distance supplement</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#fffbeb] border border-[#fde68a] p-4 mb-6">
          <h3 className="font-semibold text-[#92400e] mb-2">Cancellation Policy</h3>
          <p className="text-sm text-[#92400e]">
            Cancellations more than 24 hours before the appointment receive a full refund. Cancellations within 24 hours, or no-shows, are not refundable.
          </p>
        </div>

        <div className="bg-white border border-[#e2e8f0] p-4 mb-6 text-center">
          <p className="text-sm text-[#475569] mb-2">A confirmation email has been sent to <strong>{booking.patient_email}</strong></p>
          <p className="text-sm text-[#475569]">For questions or changes, contact us on WhatsApp:</p>
          <a href="https://wa.me/34635794368" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-[#25d366] text-white text-sm font-medium hover:bg-[#1da851] transition-colors" data-testid="whatsapp-contact">
            <MessageCircle size={16} /> +34 635 794 368
          </a>
        </div>

        <div className="text-center">
          <Link to="/" className="text-[#b8860b] hover:underline text-sm" data-testid="back-home">
            Return to homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
